import * as THREE from "three";
import { schemaParts, type FigureCard, type Schema, type Vec3 } from "./card";
import type { Figure } from "./build";
import { layeredTurn, type PoseSample } from "./pose";

// Ragdolls (spike 002, gate B5; decisions 0042, 0057; detail by tier, 0030 and 0037). On defeat a figure becomes
// jointed rigid bodies: a part list per card (data) says which joints become bodies, their shapes, masses and joint
// limits. Fuller lists for the hero and the brute, fewer parts for small ones; the tier is the list's.
//
// - Parts: a part moves a figure joint (`joint`: the joint's Object3D and its mesh follow the body; one part owns each
//   joint it names), carves a region out of a joint's merged mesh (`carve`: the triangles whose centres fall in a
//   model-space box become the part's own mesh: a robe's hem from the robe, a brute's fists from its one-joint body),
//   or spans points of a spring chain (`chain`: a scarf as a short chain of capsules). Each has a collider shape at a
//   model-space centre, a mass, and (all but the first) a parent part and a link: a spherical joint with per-axis
//   limits or a revolute hinge, at a model-space point, its limits measured from the figure's rest.
// - Sim and view apart: `spawnRagdoll` makes the bodies and joints through a small physics port (structurally
//   `@rpg/collision`'s world; this package doesn't import it) from a pose (`figurePoseFromCard`: the card's rest
//   under the pose layer's sample at a sim time, deterministic; or `figurePoseFromObject`: a live figure), clamped
//   into the limits, gives every part the figure's velocity and the hit's impulse, and returns plain data the game
//   keeps in its sim. `stepRagdoll` moves it through falling, resting (settled), dissolving and gone (its bodies
//   removed). `createRagdollView` poses a figure from the bodies each frame, carving its meshes once, and shrinks
//   them while it dissolves (the game draws the motes).
// - Checks: `ragdollLimitExcess` (against the list's limits, not what the physics holds, so a limit dropped from the
//   physics shows), `partLowest` and `ragdollBelow` (each part's lowest point against the game's ground).
//
// What Rapier holds (measured, spike 002 B5): a ball joint's per-axis limits stay within a few hundredths of a radian
// up to about ±1 rad per axis; wider ranges let the solver drift past them even in free fall, so a part that must
// swing further (a scarf folding flat under a body on its back) hangs on a hinge, whose single angle holds to ±1.6.
// Cylinders rock on their one-point contacts (use capsules, balls and boxes); thin cloth parts slide on long after
// the body has landed, so they are marked `cloth` and left out of the settle test; a damping assist after landing
// stops limbs trembling against their limits.
//
// Planted defect beside what it breaks: `RAGDOLL_FAULTS.oneLimitRemoved` spawns the first limited link without its
// limits (the soak spec's limit check must go red).

/** Planted defects: the ragdoll soak must fail with them on. */
export const RAGDOLL_FAULTS = {
  /** The first link with limits is made without them (the list still states them, so the check sees the excess). */
  oneLimitRemoved: false,
};

/** A part's collider, in the part's frame (turned by its `rot`). */
export type PartShape =
  | { type: "capsule"; halfHeight: number; radius: number }
  | { type: "ball"; radius: number }
  | { type: "cylinder"; halfHeight: number; radius: number }
  | { type: "cuboid"; half: Vec3 };

/** [min, max] radians. */
export type LimitRange = [number, number];

/** How a part hangs from its parent: a ball joint with per-axis limits, or a hinge about `axis` with limits. `at` is
 *  the model-space point (a chain part's is its first point, so it may be left out). */
export type PartLink =
  | { kind: "spherical"; at?: Vec3; limits: { x?: LimitRange; y?: LimitRange; z?: LimitRange } }
  | { kind: "revolute"; at?: Vec3; axis: Vec3; limits: LimitRange };

/** One part of a part list. */
export interface RagdollPart {
  id: string;
  /** The figure joint it moves (or carves from, with `carve`). */
  joint?: string;
  /** A model-space box: the joint mesh's triangles whose centres fall inside become this part's mesh. */
  carve?: { min: Vec3; max: Vec3 };
  /** Spans points `from` to `to` of a spring chain, as a capsule of `radius`. */
  chain?: { id: string; from: number; to: number; radius: number };
  /** The collider (not for chain parts). */
  shape?: PartShape;
  /** The collider's centre, model space at rest (not for chain parts). */
  at?: Vec3;
  /** The collider's turn, Euler XYZ. */
  rot?: Vec3;
  /** kg. */
  mass: number;
  /** This part's damping instead of the list's (cloth: a scarf, a hood point, hem tatters, which air slows). */
  damping?: { linear: number; angular: number };
  /** Cloth (a scarf, a hood point, tatters): left out of the settle test, so it may still sway as the body rests (it
   *  still stays above the ground and inside its limits). */
  cloth?: boolean;
  /** This part's friction instead of the list's (feet that slip rather than catch and wrench a hip past its limit). */
  friction?: number;
  parent?: string;
  link?: PartLink;
}

/** A card's part list and the tuning its ragdoll runs with. */
export interface PartList {
  /** Its detail tier (0030, 0037): the game's name ("full", "reduced", "minimal"). */
  tier: string;
  parts: RagdollPart[];
  damping: { linear: number; angular: number };
  friction: number;
  /** Joint friction (the physics' velocity motor toward rest, per link): limbs come to rest instead of trembling. */
  jointFriction?: number;
  /** The defeat's push: every part's speed change along the blow (m/s at severity 0 and 1) plus `lift` up, and the
   *  struck part's extra speed at the hit point (which turns the body). */
  impulse: { speed: LimitRange; lift: number; hit: LimitRange };
  /** Settled when every part but cloth is under `speed` m/s and `spin` rad/s for `hold` s; it rests `rest` s, then dissolves.
   *  Past `maxSeconds` it dissolves anyway, marked unsettled. `assist`: from `after` s on, every part is damped
   *  harder (`linear`, `angular`, reached over `ramp` s when given), as cloth and limbs on the ground would be: thin
   *  parts stop trembling, and a body that slides off a ledge late comes to rest. `sleep`: from `after` s on, a body
   *  whose parts all move under `speed` and `spin` (a creep, a tremble) is put to rest at once. */
  settle: { speed: number; spin: number; hold: number; rest: number; maxSeconds: number; assist?: { after: number; linear: number; angular: number; ramp?: number }; sleep?: { after: number; speed: number; spin: number } };
  dissolve: { seconds: number };
}

/** A part resolved against its card: model-space numbers at the card's scale. */
export interface ResolvedPart {
  id: string;
  index: number;
  /** The figure joint whose frame its body rides (a chain part's: the chain's anchor joint). */
  frame: string;
  /** The joint whose Object3D it moves (null for carve and chain parts). */
  owns: string | null;
  carve: { joint: string; min: THREE.Vector3; max: THREE.Vector3 } | null;
  chain: { id: string; from: number; to: number } | null;
  at: THREE.Vector3;
  shape: PartShape;
  /** The collider's turn in the body's frame. */
  colliderRot: THREE.Quaternion;
  mass: number;
  volume: number;
  damping: { linear: number; angular: number };
  cloth: boolean;
  friction: number;
  parent: number;
  link: (PartLink & { at: Vec3 }) | null;
}

/** A part list resolved against a card. */
export interface ResolvedRagdoll {
  list: PartList;
  parts: ResolvedPart[];
  scale: number;
  card: FigureCard;
  /** Each joint's rest position, model space at scale. */
  rest: Map<string, THREE.Vector3>;
}

const v3 = (v: Readonly<Vec3>, k = 1): THREE.Vector3 => new THREE.Vector3(v[0] * k, v[1] * k, v[2] * k);
const UP = new THREE.Vector3(0, 1, 0);

function volumeOf(s: PartShape): number {
  switch (s.type) {
    case "ball":
      return (4 / 3) * Math.PI * s.radius ** 3;
    case "capsule":
      return Math.PI * s.radius ** 2 * 2 * s.halfHeight + (4 / 3) * Math.PI * s.radius ** 3;
    case "cylinder":
      return Math.PI * s.radius ** 2 * 2 * s.halfHeight;
    case "cuboid":
      return 8 * s.half[0] * s.half[1] * s.half[2];
  }
}

function scaleShape(s: PartShape, k: number): PartShape {
  switch (s.type) {
    case "ball":
      return { type: "ball", radius: s.radius * k };
    case "capsule":
      return { type: "capsule", halfHeight: s.halfHeight * k, radius: s.radius * k };
    case "cylinder":
      return { type: "cylinder", halfHeight: s.halfHeight * k, radius: s.radius * k };
    case "cuboid":
      return { type: "cuboid", half: [s.half[0] * k, s.half[1] * k, s.half[2] * k] };
  }
}

/** Check a part list against its card and resolve it at the card's scale. Throws naming the part and the problem. */
export function resolveRagdoll(card: FigureCard, list: PartList): ResolvedRagdoll {
  const k = card.scale ?? 1;
  const joints = new Map(card.joints.map((j) => [j.id, j]));
  const rest = new Map<string, THREE.Vector3>(card.joints.map((j) => [j.id, v3(j.at, k)]));
  const chains = new Map((card.chains ?? []).map((c) => [c.id, c]));
  const index = new Map<string, number>();
  const owners = new Map<string, string>();
  const named = new Set<string>();
  const parts: ResolvedPart[] = [];
  const range = (r: LimitRange | undefined, id: string): void => {
    if (r && !(r[0] <= 0 && 0 <= r[1] && r[0] > -Math.PI && r[1] < Math.PI)) throw new Error(`@rpg/figure: ragdoll part "${id}" has limits ${JSON.stringify(r)}: want min ≤ 0 ≤ max inside (-π, π)`);
  };
  if (!list.parts.length) throw new Error("@rpg/figure: a ragdoll part list has no parts");
  list.parts.forEach((p, i) => {
    const bad = (why: string): never => {
      throw new Error(`@rpg/figure: ragdoll part "${p.id}" ${why}`);
    };
    if (index.has(p.id)) bad("is listed twice");
    if (!!p.joint === !!p.chain) bad("needs one of `joint` or `chain`");
    if (!(p.mass > 0)) bad("needs a mass above 0");
    let frame: string;
    let at: THREE.Vector3;
    let shape: PartShape;
    let colliderRot = new THREE.Quaternion();
    let chain: ResolvedPart["chain"] = null;
    if (p.chain) {
      const c = chains.get(p.chain.id) ?? bad(`spans unknown chain "${p.chain.id}"`);
      const { from, to, radius } = p.chain;
      if (!(Number.isInteger(from) && Number.isInteger(to) && from >= 0 && to > from && to < c.points.length)) bad(`spans points ${from} to ${to} of a ${c.points.length}-point chain`);
      const a = v3(c.points[from]!, k);
      const b = v3(c.points[to]!, k);
      const len = a.distanceTo(b);
      frame = c.joint;
      at = a.clone().add(b).multiplyScalar(0.5);
      const r = radius * k;
      shape = { type: "capsule", halfHeight: Math.max(0.005, len / 2 - r), radius: r };
      colliderRot = new THREE.Quaternion().setFromUnitVectors(UP, b.clone().sub(a).normalize());
      chain = { id: c.id, from, to };
    } else {
      const j = p.joint!;
      if (!joints.has(j)) bad(`names unknown joint "${j}"`);
      if (!p.shape || !p.at) bad("needs a shape and an `at`");
      frame = j;
      named.add(j);
      if (!p.carve) {
        if (owners.has(j)) bad(`moves joint "${j}", already moved by part "${owners.get(j)}"`);
        owners.set(j, p.id);
      }
      at = v3(p.at!, k);
      shape = scaleShape(p.shape!, k);
      if (p.rot) colliderRot = new THREE.Quaternion().setFromEuler(new THREE.Euler(p.rot[0], p.rot[1], p.rot[2], "XYZ"));
    }
    let parent = -1;
    let link: ResolvedPart["link"] = null;
    if (i === 0) {
      if (p.parent || p.link) bad("is the root: it takes no parent or link");
    } else {
      if (!p.parent || !index.has(p.parent)) bad(p.parent ? `names parent "${p.parent}", not listed before it` : "needs a parent (only the first part is the root)");
      if (!p.link) bad("needs a link to its parent");
      parent = index.get(p.parent!)!;
      const l = p.link!;
      const lat = chain ? (chains.get(chain.id)!.points[chain.from]! as Vec3) : (l.at ?? bad("needs its link's `at`"));
      if (l.kind === "spherical") {
        range(l.limits.x, p.id);
        range(l.limits.y, p.id);
        range(l.limits.z, p.id);
      } else {
        range(l.limits, p.id);
        if (!(Math.hypot(...l.axis) > 1e-9)) bad("has a zero hinge axis");
      }
      link = { ...structuredClone(l), at: [lat[0] * k, lat[1] * k, lat[2] * k] };
    }
    index.set(p.id, i);
    parts.push({
      id: p.id,
      index: i,
      frame,
      owns: p.joint && !p.carve ? p.joint : null,
      carve: p.carve ? { joint: p.joint!, min: v3(p.carve.min, k), max: v3(p.carve.max, k) } : null,
      chain,
      at,
      shape,
      colliderRot,
      mass: p.mass * k ** 3,
      volume: volumeOf(shape),
      damping: p.damping ?? list.damping,
      cloth: p.cloth ?? false,
      friction: p.friction ?? list.friction,
      parent,
      link,
    });
  });
  for (const j of named) if (!owners.has(j)) throw new Error(`@rpg/figure: ragdoll joint "${j}" is carved but no part moves it (give one part on it no \`carve\`)`);
  // a chain's parts cover its points end to end
  const spans = new Map<string, [number, number][]>();
  for (const p of parts) if (p.chain) spans.set(p.chain.id, [...(spans.get(p.chain.id) ?? []), [p.chain.from, p.chain.to]]);
  for (const [id, s] of spans) {
    s.sort((a, b) => a[0] - b[0]);
    const n = chains.get(id)!.points.length;
    if (s[0]![0] !== 0 || s[s.length - 1]![1] !== n - 1 || s.some((x, i) => i > 0 && x[0] !== s[i - 1]![1])) throw new Error(`@rpg/figure: ragdoll chain "${id}" parts must cover its points 0 to ${n - 1} end to end`);
  }
  return { list, parts, scale: k, card, rest };
}

// ---------------------------------------------------------------------------------------------------------------
// Poses

/** A joint's world pose. */
export interface JointPose {
  p: THREE.Vector3;
  q: THREE.Quaternion;
}

/** Every joint's world pose from the card: the rest pose (joints carry no rest turn) under a pose-layer sample (null:
 *  rest), the root at `position` turned by `yaw` about +y. Pure: a sim can call it at a step. */
export function figurePoseFromCard(card: FigureCard, root: { position: Vec3; yaw: number }, sample: PoseSample | null = null): Map<string, JointPose> {
  const k = card.scale ?? 1;
  const rootM = new THREE.Matrix4().compose(v3(root.position), new THREE.Quaternion().setFromAxisAngle(UP, root.yaw), new THREE.Vector3(1, 1, 1));
  if (sample) {
    // the pivot: turn about the pose's pivot point and offset (pose.ts)
    const r = sample.root;
    const pv = r.pivot.clone().multiplyScalar(k);
    const pos = r.offset.clone().multiplyScalar(k).add(pv).sub(pv.clone().applyQuaternion(r.rot));
    rootM.multiply(new THREE.Matrix4().compose(pos, r.rot, new THREE.Vector3(1, 1, 1)));
  }
  const specs = new Map(card.joints.map((j) => [j.id, j]));
  const world = new Map<string, THREE.Matrix4>();
  const out = new Map<string, JointPose>();
  const id = new THREE.Quaternion();
  const walk = (jid: string): THREE.Matrix4 => {
    const have = world.get(jid);
    if (have) return have;
    const s = specs.get(jid)!;
    const parentM = s.parent ? walk(s.parent) : rootM;
    const local = v3(s.at, k).sub(s.parent ? v3(specs.get(s.parent)!.at, k) : new THREE.Vector3());
    const q = sample ? layeredTurn(id, sample.joints.get(jid)) : new THREE.Quaternion();
    const m = parentM.clone().multiply(new THREE.Matrix4().compose(local, q, new THREE.Vector3(1, 1, 1)));
    world.set(jid, m);
    const p = new THREE.Vector3();
    const wq = new THREE.Quaternion();
    m.decompose(p, wq, new THREE.Vector3());
    out.set(jid, { p, q: wq });
    return m;
  };
  for (const j of card.joints) walk(j.id);
  return out;
}

/** Every joint's world pose from a live figure (its joints' world matrices). */
export function figurePoseFromObject(fig: Figure): Map<string, JointPose> {
  fig.root.updateMatrixWorld(true);
  const out = new Map<string, JointPose>();
  for (const [id, j] of fig.joints) {
    const p = new THREE.Vector3();
    const q = new THREE.Quaternion();
    j.matrixWorld.decompose(p, q, new THREE.Vector3());
    out.set(id, { p, q });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// The physics port and spawning

type XYZ = { x: number; y: number; z: number };
type XYZW = XYZ & { w: number };

/** A collider as the physics port takes it (`@rpg/collision`'s shapes). */
export type PortShape =
  | { type: "cuboid"; halfExtents: XYZ }
  | { type: "ball"; radius: number }
  | { type: "capsule"; halfHeight: number; radius: number }
  | { type: "cylinder"; halfHeight: number; radius: number };

/** A body as the physics port takes it. */
export interface PortBody {
  id: string;
  kind: "dynamic";
  position: XYZ;
  rotation: XYZW;
  colliders: { shape: PortShape; layer: string; rotation?: XYZW; density?: number; friction?: number; restitution?: number }[];
  linearDamping?: number;
  angularDamping?: number;
  ccd?: boolean;
}

/** A joint as the physics port takes it. */
export type PortJoint =
  | { id: string; kind: "spherical"; a: string; b: string; anchorA: XYZ; anchorB: XYZ; limits?: { x?: LimitRange; y?: LimitRange; z?: LimitRange }; friction?: number }
  | { id: string; kind: "revolute"; a: string; b: string; anchorA: XYZ; anchorB: XYZ; axis: XYZ; limits?: LimitRange; friction?: number };

/** What a ragdoll needs from the physics: `@rpg/collision`'s `CollisionWorld` has it. */
export interface RagdollPhysics {
  add(spec: PortBody): void;
  addJoint(spec: PortJoint): void;
  remove(id: string): void;
  has(id: string): boolean;
  pose(id: string): { position: XYZ; rotation: XYZW };
  velocity(id: string): { linear: XYZ; angular: XYZ };
  setVelocity(id: string, linear: XYZ, angular?: XYZ): void;
  setDamping(id: string, linear: number, angular: number): void;
  /** Put a body to sleep (its velocities zeroed). */
  sleep(id: string): void;
  applyImpulse(id: string, impulse: XYZ, point?: XYZ): void;
  jointAngles(id: string): { kind: "spherical"; angles: XYZ } | { kind: "revolute"; angle: number };
}

/** A ragdoll's phases. */
export type RagdollPhase = "falling" | "resting" | "dissolving" | "gone";

/** A live ragdoll: plain data a game keeps (and saves) in its sim. */
export interface RagdollState {
  id: string;
  tier: string;
  phase: RagdollPhase;
  /** Seconds since it spawned. */
  age: number;
  /** Seconds every part has been still. */
  quiet: number;
  /** Its age when it settled, or null. */
  settledAt: number | null;
  /** Its age when the phase began. */
  phaseAt: number;
  /** It hit `settle.maxSeconds` without settling. */
  unsettled: boolean;
  /** Body IDs, one per part in list order; joint IDs, one per linked part (in list order). */
  bodies: string[];
  joints: string[];
}

/** How a ragdoll starts. */
export interface SpawnOptions {
  /** Its ID: the prefix of its bodies' and joints' IDs. */
  id: string;
  /** The physics layer its colliders sit on. */
  layer: string;
  /** Where every joint is (`figurePoseFromCard` or `figurePoseFromObject`). */
  pose: Map<string, JointPose>;
  /** The figure's velocity, given to every part. */
  velocity?: Readonly<Vec3>;
  /** The defeating blow: its direction (unit), severity (0 to 1) and point (world). */
  hit?: { dir: Readonly<Vec3>; severity: number; point: Readonly<Vec3> };
}

const clampTurn = (rel: THREE.Quaternion, link: PartLink): THREE.Quaternion => {
  if (rel.w < 0) rel.set(-rel.x, -rel.y, -rel.z, -rel.w);
  if (link.kind === "revolute") {
    const ax = v3(link.axis).normalize();
    let a = 2 * Math.atan2(rel.x * ax.x + rel.y * ax.y + rel.z * ax.z, rel.w);
    if (a > Math.PI) a -= 2 * Math.PI;
    a = Math.min(link.limits[1], Math.max(link.limits[0], a));
    return rel.setFromAxisAngle(ax, a);
  }
  const s = (r: LimitRange | undefined, v: number): number => (r ? Math.min(Math.sin(r[1] / 2), Math.max(Math.sin(r[0] / 2), v)) : v);
  const x = s(link.limits.x, rel.x);
  const y = s(link.limits.y, rel.y);
  const z = s(link.limits.z, rel.z);
  const w = Math.sqrt(Math.max(0, 1 - x * x - y * y - z * z));
  return rel.set(x, y, z, w).normalize();
};

const xyz = (v: THREE.Vector3): XYZ => ({ x: v.x, y: v.y, z: v.z });
const xyzw = (q: THREE.Quaternion): XYZW => ({ x: q.x, y: q.y, z: q.z, w: q.w });

function portShape(s: PartShape): PortShape {
  return s.type === "cuboid" ? { type: "cuboid", halfExtents: { x: s.half[0], y: s.half[1], z: s.half[2] } } : s;
}

/** Each part's body pose at spawn: the part's frame joint's pose, offset to the collider centre, each part's turn
 *  against its parent clamped into its link's limits (parents first). */
export function seedParts(rd: ResolvedRagdoll, pose: Map<string, JointPose>): JointPose[] {
  const out: JointPose[] = [];
  for (const p of rd.parts) {
    const j = pose.get(p.frame);
    if (!j) throw new Error(`@rpg/figure: no pose for joint "${p.frame}"`);
    const q = j.q.clone();
    if (p.parent >= 0 && p.link) {
      const pq = out[p.parent]!.q;
      const rel = clampTurn(pq.clone().invert().multiply(q), p.link);
      q.copy(pq).multiply(rel);
    }
    const off = p.at.clone().sub(rd.rest.get(p.frame)!).applyQuaternion(j.q);
    out.push({ p: j.p.clone().add(off), q });
  }
  return out;
}

/** Make a ragdoll's bodies and joints, give them the figure's velocity and the blow's impulse. */
export function spawnRagdoll(world: RagdollPhysics, rd: ResolvedRagdoll, o: SpawnOptions): RagdollState {
  const L = rd.list;
  const seeds = seedParts(rd, o.pose);
  const bodies: string[] = [];
  const joints: string[] = [];
  rd.parts.forEach((p, i) => {
    const id = `${o.id}:${p.id}`;
    const s = seeds[i]!;
    world.add({
      id,
      kind: "dynamic",
      position: xyz(s.p),
      rotation: xyzw(s.q),
      colliders: [{ shape: portShape(p.shape), layer: o.layer, rotation: xyzw(p.colliderRot), density: p.mass / p.volume, friction: p.friction, restitution: 0 }],
      linearDamping: p.damping.linear,
      angularDamping: p.damping.angular,
      // small parts get continuous collision detection, so a fast fall can't tunnel through the ground
      ccd: p.shape.type === "ball" ? p.shape.radius < 0.08 : p.shape.type === "cuboid" ? Math.min(...p.shape.half) < 0.05 : p.shape.radius < 0.06,
    });
    bodies.push(id);
  });
  let dropped = false;
  rd.parts.forEach((p, i) => {
    if (p.parent < 0 || !p.link) return;
    const parent = rd.parts[p.parent]!;
    const id = `${o.id}:${p.id}`;
    const at = v3(p.link.at);
    // anchors in each body's frame: the link point relative to the collider centre, in the model's (rest) axes
    const anchorA = xyz(at.clone().sub(parent.at));
    const anchorB = xyz(at.clone().sub(p.at));
    // the planted defect lives here, beside the limits it drops
    const drop = RAGDOLL_FAULTS.oneLimitRemoved && !dropped;
    if (drop) dropped = true;
    if (p.link.kind === "spherical") {
      world.addJoint({ id, kind: "spherical", a: bodies[p.parent]!, b: bodies[i]!, anchorA, anchorB, friction: L.jointFriction ?? 0, ...(drop ? {} : { limits: p.link.limits }) });
    } else {
      world.addJoint({ id, kind: "revolute", a: bodies[p.parent]!, b: bodies[i]!, anchorA, anchorB, axis: xyz(v3(p.link.axis)), friction: L.jointFriction ?? 0, ...(drop ? {} : { limits: p.link.limits }) });
    }
    joints.push(id);
  });
  // the figure's velocity, then the blow: a speed change for every part along it (no turn), and the struck part's
  // extra at the hit point (the turn that topples it)
  const vel = o.velocity ?? [0, 0, 0];
  const hit = o.hit;
  const sev = hit ? Math.min(1, Math.max(0, hit.severity)) : 0;
  const along = hit ? v3(hit.dir).setY(0) : new THREE.Vector3();
  if (along.lengthSq() > 1e-9) along.normalize();
  const dv = along.clone().multiplyScalar(hit ? L.impulse.speed[0] + (L.impulse.speed[1] - L.impulse.speed[0]) * sev : 0);
  if (hit) dv.y += L.impulse.lift;
  rd.parts.forEach((p, i) => {
    world.setVelocity(bodies[i]!, { x: vel[0], y: vel[1], z: vel[2] }, { x: 0, y: 0, z: 0 });
    if (hit) world.applyImpulse(bodies[i]!, xyz(dv.clone().multiplyScalar(p.mass)));
  });
  if (hit) {
    const point = v3(hit.point);
    let best = 0;
    let bestD = Infinity;
    seeds.forEach((s, i) => {
      const d = s.p.distanceTo(point);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    const p = rd.parts[best]!;
    const extra = L.impulse.hit[0] + (L.impulse.hit[1] - L.impulse.hit[0]) * sev;
    world.applyImpulse(bodies[best]!, xyz(v3(hit.dir).normalize().multiplyScalar(p.mass * extra)), xyz(point));
  }
  return { id: o.id, tier: L.tier, phase: "falling", age: 0, quiet: 0, settledAt: null, phaseAt: 0, unsettled: false, bodies, joints };
}

const len = (v: XYZ): number => Math.hypot(v.x, v.y, v.z);

/** Advance a ragdoll by one step's `dt` (after the physics step): falling until every part is still for `hold` s (or
 *  `maxSeconds`), resting, dissolving, then gone with its bodies (and joints) removed. */
export function stepRagdoll(world: RagdollPhysics, s: RagdollState, rd: ResolvedRagdoll, dt: number): void {
  if (s.phase === "gone") return;
  const L = rd.list;
  s.age += dt;
  if (s.phase === "falling") {
    const assist = L.settle.assist;
    if (assist && s.age >= assist.after && s.age - dt < assist.after + (assist.ramp ?? 0)) {
      // from `after`, each part's damping rises to the assist's over `ramp` seconds (at once without one)
      const k = assist.ramp ? Math.min(1, (s.age - assist.after) / assist.ramp) : 1;
      rd.parts.forEach((p, i) =>
        world.setDamping(s.bodies[i]!, p.damping.linear + Math.max(0, assist.linear - p.damping.linear) * k, p.damping.angular + Math.max(0, assist.angular - p.damping.angular) * k),
      );
    }
    let lin = 0;
    let ang = 0;
    rd.parts.forEach((p, i) => {
      if (p.cloth) return;
      const v = world.velocity(s.bodies[i]!);
      lin = Math.max(lin, len(v.linear));
      ang = Math.max(ang, len(v.angular));
    });
    // `sleep`: from `after` s on, a body moving only slowly (a creep, a tremble) is put to rest, as a physics engine's
    // own sleep would after a while
    const sl = L.settle.sleep;
    if (sl && s.age >= sl.after && lin < sl.speed && ang < sl.spin && (lin >= L.settle.speed || ang >= L.settle.spin)) {
      for (const b of s.bodies) world.sleep(b);
      lin = 0;
      ang = 0;
    }
    s.quiet = lin < L.settle.speed && ang < L.settle.spin ? s.quiet + dt : 0;
    if (s.quiet >= L.settle.hold) {
      s.settledAt = s.age;
      s.phase = "resting";
      s.phaseAt = s.age;
    } else if (s.age >= L.settle.maxSeconds) {
      s.unsettled = true;
      s.phase = "dissolving";
      s.phaseAt = s.age;
    }
    return;
  }
  if (s.phase === "resting") {
    if (s.age - s.phaseAt >= L.settle.rest) {
      s.phase = "dissolving";
      s.phaseAt = s.age;
    }
    return;
  }
  if (s.age - s.phaseAt >= L.dissolve.seconds) {
    for (const b of s.bodies) world.remove(b);
    s.phase = "gone";
    s.phaseAt = s.age;
  }
}

/** How far through its dissolve a ragdoll is (0 before, 1 when gone). */
export function dissolveProgress(s: RagdollState, rd: ResolvedRagdoll): number {
  if (s.phase === "gone") return 1;
  if (s.phase !== "dissolving") return 0;
  return Math.min(1, (s.age - s.phaseAt) / Math.max(1e-6, rd.list.dissolve.seconds));
}

// ---------------------------------------------------------------------------------------------------------------
// Checks

const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();

/** A part's lowest world point, given its body's pose. */
export function partLowest(p: ResolvedPart, pose: { position: XYZ; rotation: XYZW }): THREE.Vector3 {
  const c = new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z);
  const q = _q.set(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w).multiply(p.colliderRot);
  const s = p.shape;
  if (s.type === "ball") return c.setY(c.y - s.radius);
  if (s.type === "cuboid") {
    let low: THREE.Vector3 | null = null;
    for (const sx of [-1, 1])
      for (const sy of [-1, 1])
        for (const sz of [-1, 1]) {
          const corner = _v.set(sx * s.half[0], sy * s.half[1], sz * s.half[2]).applyQuaternion(q).add(c);
          if (!low || corner.y < low.y) low = corner.clone();
        }
    return low!;
  }
  const axis = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
  const end = c.clone().addScaledVector(axis, axis.y > 0 ? -s.halfHeight : s.halfHeight);
  if (s.type === "capsule") return end.setY(end.y - s.radius);
  // a cylinder: the lower end's rim point furthest down
  const down = new THREE.Vector3(0, -1, 0).addScaledVector(axis, axis.y);
  if (down.lengthSq() > 1e-9) end.addScaledVector(down.normalize(), s.radius);
  return end;
}

/** The deepest any part's lowest point sits under `ground(x, z)` (m; ≤ 0 when none is under), and which part. */
export function ragdollBelow(world: Pick<RagdollPhysics, "pose" | "has">, s: RagdollState, rd: ResolvedRagdoll, ground: (x: number, z: number) => number): { depth: number; part: string | null } {
  let depth = -Infinity;
  let part: string | null = null;
  rd.parts.forEach((p, i) => {
    const b = s.bodies[i]!;
    if (!world.has(b)) return;
    const low = partLowest(p, world.pose(b));
    const d = ground(low.x, low.z) - low.y;
    if (d > depth) {
      depth = d;
      part = p.id;
    }
  });
  return { depth, part };
}

const outside = (a: number, r: LimitRange | undefined): number => (r ? Math.max(0, r[0] - a, a - r[1]) : 0);

/** The worst excess (radians) of any linked part past its list's limits (not the physics': a dropped limit shows). */
export function ragdollLimitExcess(world: Pick<RagdollPhysics, "jointAngles" | "has">, s: RagdollState, rd: ResolvedRagdoll): { excess: number; part: string | null } {
  let excess = 0;
  let part: string | null = null;
  let j = 0;
  for (const p of rd.parts) {
    if (p.parent < 0 || !p.link) continue;
    const id = s.joints[j++]!;
    if (!world.has(s.bodies[p.index]!)) continue;
    const a = world.jointAngles(id);
    const e =
      a.kind === "revolute" && p.link.kind === "revolute"
        ? outside(a.angle, p.link.limits)
        : a.kind === "spherical" && p.link.kind === "spherical"
          ? Math.max(outside(a.angles.x, p.link.limits.x), outside(a.angles.y, p.link.limits.y), outside(a.angles.z, p.link.limits.z))
          : 0;
    if (e > excess) {
      excess = e;
      part = p.id;
    }
  }
  return { excess, part };
}

// ---------------------------------------------------------------------------------------------------------------
// The view

/** A figure posed from its ragdoll's bodies. */
export interface RagdollView {
  /** Pose the figure from the bodies (those still there), step the chains no part spans, shrink while dissolving. */
  update(world: Pick<RagdollPhysics, "pose" | "has">, s: RagdollState, dt: number): void;
  /** Each part's centre now (world): where motes rise from. */
  centres(): THREE.Vector3[];
  /** The carved meshes, for counting. */
  readonly carved: THREE.Mesh[];
}

/** Swap a figure to its ragdoll's view: carve the meshes the list carves, show every moved joint, zero the pose
 *  pivot. The figure is the ragdoll's from now on (dispose it when the ragdoll is gone). */
export function createRagdollView(fig: Figure, rd: ResolvedRagdoll): RagdollView {
  const k = rd.scale;
  fig.pivot.position.set(0, 0, 0);
  fig.pivot.quaternion.identity();
  // joints back to their rest offsets (the motion stretched legs and moved hands); turns are set below or kept
  for (const [id, j] of fig.joints) {
    const parent = fig.card.joints.find((x) => x.id === id)?.parent;
    j.position.copy(fig.rest.get(id)!).sub(parent ? fig.rest.get(parent)! : new THREE.Vector3());
  }
  for (const m of fig.meshes.values()) m.scale.set(1, 1, 1);

  // carve: per joint, each triangle to the first carving part whose box holds its centre, the rest to the joint
  const carved: THREE.Mesh[] = [];
  const holders: (THREE.Object3D | null)[] = rd.parts.map(() => null);
  const byJoint = new Map<string, ResolvedPart[]>();
  for (const p of rd.parts) if (p.carve) byJoint.set(p.carve.joint, [...(byJoint.get(p.carve.joint) ?? []), p]);
  for (const [joint, cutters] of byJoint) {
    const mesh = fig.meshes.get(joint);
    if (!mesh) continue;
    const src = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const P = src.getAttribute("position");
    const tris = P.count / 3;
    const owner = new Int32Array(tris).fill(-1);
    const rest = fig.rest.get(joint)!;
    const c = new THREE.Vector3();
    for (let t = 0; t < tris; t++) {
      c.set(0, 0, 0);
      for (let v = 0; v < 3; v++) c.add(_v.fromBufferAttribute(P, t * 3 + v));
      c.multiplyScalar(1 / 3).add(rest);
      const i = cutters.findIndex((p) => c.x >= p.carve!.min.x && c.x <= p.carve!.max.x && c.y >= p.carve!.min.y && c.y <= p.carve!.max.y && c.z >= p.carve!.min.z && c.z <= p.carve!.max.z);
      owner[t] = i;
    }
    const pick = (want: number): THREE.BufferGeometry => {
      const g = new THREE.BufferGeometry();
      for (const [name, attr] of Object.entries(src.attributes)) {
        const a = attr as THREE.BufferAttribute;
        const out: number[] = [];
        for (let t = 0; t < tris; t++) if (owner[t] === want) for (let v = 0; v < 3; v++) for (let e = 0; e < a.itemSize; e++) out.push(a.array[(t * 3 + v) * a.itemSize + e]!);
        g.setAttribute(name, new THREE.BufferAttribute(new Float32Array(out), a.itemSize));
      }
      return g;
    };
    cutters.forEach((p, i) => {
      const m = new THREE.Mesh(pick(i), mesh.material);
      m.name = `${p.id}:carved`;
      m.castShadow = mesh.castShadow;
      m.receiveShadow = mesh.receiveShadow;
      const holder = new THREE.Group();
      holder.name = `${p.id}:part`;
      holder.add(m);
      fig.root.add(holder);
      holders[p.index] = holder;
      carved.push(m);
    });
    const restGeo = pick(-1);
    mesh.geometry.dispose();
    mesh.geometry = restGeo;
  }
  // the joints the parts move, parents first
  const depth = (o: THREE.Object3D): number => {
    let d = 0;
    for (let x = o.parent; x && x !== fig.root; x = x.parent) d++;
    return d;
  };
  const moved = rd.parts.filter((p) => p.owns).sort((a, b) => depth(fig.joints.get(a.owns!)!) - depth(fig.joints.get(b.owns!)!));
  for (const p of moved) fig.joints.get(p.owns!)!.visible = true;
  // chains: the parts spanning each, and the chains no part spans (their springs keep running)
  const chainParts = new Map<string, ResolvedPart[]>();
  for (const p of rd.parts) if (p.chain) chainParts.set(p.chain.id, [...(chainParts.get(p.chain.id) ?? []), p]);
  const freeChains = [...fig.chains.values()].filter((c) => !chainParts.has(c.spec.id));

  const bodyM = new THREE.Matrix4();
  const target = new THREE.Matrix4();
  const inv = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const rot = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const one = new THREE.Vector3(1, 1, 1);
  const centres = rd.parts.map(() => new THREE.Vector3());
  let dissolveBase: Map<THREE.Mesh, THREE.Vector3> | null = null;

  const placeAt = (obj: THREE.Object3D, m: THREE.Matrix4): void => {
    inv.copy(obj.parent!.matrixWorld).invert();
    target.multiplyMatrices(inv, m).decompose(pos, rot, scl);
    obj.position.copy(pos);
    obj.quaternion.copy(rot);
    obj.updateMatrixWorld(true);
  };

  return {
    carved,
    update(world, s, dt) {
      fig.root.updateMatrixWorld(true);
      const poses = rd.parts.map((p, i) => {
        const b = s.bodies[i]!;
        if (!world.has(b)) return null;
        const bp = world.pose(b);
        centres[i]!.set(bp.position.x, bp.position.y, bp.position.z);
        return bp;
      });
      const bodyMatrix = (i: number): THREE.Matrix4 | null => {
        const bp = poses[i];
        if (!bp) return null;
        return bodyM.compose(_v.set(bp.position.x, bp.position.y, bp.position.z), rot.set(bp.rotation.x, bp.rotation.y, bp.rotation.z, bp.rotation.w), one);
      };
      // a part's joint frame sits at (rest - at) from its collider centre, unturned
      const jointFrame = (p: ResolvedPart, frameJoint: string): THREE.Matrix4 | null => {
        const m = bodyMatrix(p.index);
        if (!m) return null;
        const off = fig.rest.get(frameJoint)!.clone().sub(p.at);
        return m.clone().multiply(new THREE.Matrix4().makeTranslation(off.x, off.y, off.z));
      };
      for (const p of moved) {
        const m = jointFrame(p, p.owns!);
        if (m) placeAt(fig.joints.get(p.owns!)!, m);
      }
      for (const p of rd.parts) {
        const h = holders[p.index];
        if (!h || !p.carve) continue;
        const m = jointFrame(p, p.carve.joint);
        if (m) placeAt(h, m);
      }
      for (const [id, parts] of chainParts) {
        const chain = fig.chains.get(id);
        if (!chain) continue;
        const pts = chain.spec.points.map(() => new THREE.Vector3());
        let ok = true;
        for (const p of parts) {
          const m = bodyMatrix(p.index);
          if (!m) {
            ok = false;
            continue;
          }
          for (let i = p.chain!.from; i <= p.chain!.to; i++) pts[i]!.set(...(chain.spec.points[i]! as Vec3)).sub(p.at).applyMatrix4(m);
        }
        if (ok) chain.follow(pts);
      }
      for (const c of freeChains) c.update(dt);
      // dissolving: every mesh shrinks about its own centre
      const t = dissolveProgress(s, rd);
      if (t > 0) {
        dissolveBase ??= new Map();
        const sc = 1 - t * t * (3 - 2 * t);
        fig.root.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          if (!m.geometry.boundingSphere) m.geometry.computeBoundingSphere();
          const ctr = m.geometry.boundingSphere!.center;
          if (!dissolveBase!.has(m)) dissolveBase!.set(m, m.position.clone());
          m.scale.setScalar(Math.max(1e-3, sc));
          m.position.copy(dissolveBase!.get(m)!).addScaledVector(ctr, 1 - sc);
        });
      }
    },
    centres: () => centres.map((c) => c.clone()),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// The one-call seam: a live figure to a ragdoll

/** A figure handed to the physics: its state (plain data), its view, and a step for both. */
export interface Ragdoll {
  state: RagdollState;
  view: RagdollView;
  /** After each physics step: advance the phases, then pose the figure. */
  step(dt: number): void;
}

/**
 * Swap a defeated figure to its ragdoll: bodies seeded from the figure's pose now (its joints' world matrices), the
 * blow's impulse applied, the figure posed from the bodies from then on. For a game that keeps the ragdoll in its view
 * (a deterministic sim seeds from `figurePoseFromCard` and calls `spawnRagdoll` itself).
 */
export function ragdoll(figure: Figure, partList: PartList, impulse: SpawnOptions["hit"] | null, world: RagdollPhysics, o: { id: string; layer: string; velocity?: Vec3 }): Ragdoll {
  const rd = resolveRagdoll(figure.card, partList);
  const state = spawnRagdoll(world, rd, { id: o.id, layer: o.layer, pose: figurePoseFromObject(figure), ...(o.velocity ? { velocity: o.velocity } : {}), ...(impulse ? { hit: impulse } : {}) });
  const view = createRagdollView(figure, rd);
  return {
    state,
    view,
    step(dt) {
      stepRagdoll(world, state, rd, dt);
      view.update(world, state, dt);
    },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// The schema (draft-07): a part list as content.

const { num, obj, list, vec, tuple, JOINT_ID, TAU } = schemaParts;
const RANGE = tuple([num(-Math.PI + 1e-3, 0), num(0, Math.PI - 1e-3)], "[min ≤ 0, max ≥ 0] radians");

const SHAPE: Schema = {
  type: "object",
  required: ["type"],
  properties: { type: { enum: ["capsule", "ball", "cylinder", "cuboid"] } },
  allOf: [
    { if: { properties: { type: { const: "capsule" } } }, then: obj({ type: { const: "capsule" }, halfHeight: num(0.001, 2), radius: num(0.005, 2) }, ["type", "halfHeight", "radius"]) },
    { if: { properties: { type: { const: "cylinder" } } }, then: obj({ type: { const: "cylinder" }, halfHeight: num(0.001, 2), radius: num(0.005, 2) }, ["type", "halfHeight", "radius"]) },
    { if: { properties: { type: { const: "ball" } } }, then: obj({ type: { const: "ball" }, radius: num(0.005, 2) }, ["type", "radius"]) },
    { if: { properties: { type: { const: "cuboid" } } }, then: obj({ type: { const: "cuboid" }, half: vec(0.001, 2) }, ["type", "half"]) },
  ],
};

const LINK: Schema = {
  type: "object",
  required: ["kind"],
  properties: { kind: { enum: ["spherical", "revolute"] } },
  allOf: [
    {
      if: { properties: { kind: { const: "spherical" } } },
      then: obj({ kind: { const: "spherical" }, at: vec(-4, 4), limits: obj({ x: RANGE, y: RANGE, z: RANGE }, []) }, ["kind", "limits"]),
    },
    { if: { properties: { kind: { const: "revolute" } } }, then: obj({ kind: { const: "revolute" }, at: vec(-4, 4), axis: vec(-1, 1), limits: RANGE }, ["kind", "axis", "limits"]) },
  ],
};

const PART = obj(
  {
    id: { type: "string" },
    joint: JOINT_ID,
    carve: obj({ min: vec(-4, 4), max: vec(-4, 4) }, ["min", "max"], "Model-space box: the joint mesh's triangles centred inside"),
    chain: obj({ id: JOINT_ID, from: { type: "integer", minimum: 0, maximum: 15 }, to: { type: "integer", minimum: 1, maximum: 15 }, radius: num(0.005, 0.5) }, ["id", "from", "to", "radius"]),
    shape: SHAPE,
    at: vec(-4, 4, "Collider centre, model space"),
    rot: vec(-TAU, TAU),
    mass: num(0.01, 500, "kg"),
    damping: obj({ linear: num(0, 20), angular: num(0, 20) }, ["linear", "angular"], "This part's damping instead of the list's"),
    cloth: { type: "boolean", description: "Cloth: left out of the settle test" },
    friction: num(0, 4, "This part's friction instead of the list's"),
    parent: { type: "string" },
    link: LINK,
  },
  ["id", "mass"],
);

/** The part list's JSON Schema; `extra` adds the game's fields (an id, the card it belongs to). */
export function partListSchema(extra: { properties?: Record<string, Schema>; required?: string[]; title?: string; description?: string } = {}): Schema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "PartList",
    ...(extra.description ? { description: extra.description } : {}),
    type: "object",
    additionalProperties: false,
    required: [...(extra.required ?? []), "tier", "parts", "damping", "friction", "impulse", "settle", "dissolve"],
    properties: {
      ...(extra.properties ?? {}),
      tier: { type: "string" },
      parts: list(PART, 1, 24),
      damping: obj({ linear: num(0, 20), angular: num(0, 20) }, ["linear", "angular"]),
      friction: num(0, 4),
      jointFriction: num(0, 100, "The joints' velocity-motor factor toward rest"),
      impulse: obj({ speed: tuple([num(0, 30), num(0, 30)]), lift: num(0, 20), hit: tuple([num(0, 30), num(0, 30)]) }, ["speed", "lift", "hit"]),
      settle: obj(
        {
          speed: num(0.001, 2, "m/s"),
          spin: num(0.001, 10, "rad/s"),
          hold: num(0, 3),
          rest: num(0, 5),
          maxSeconds: num(0.5, 20),
          assist: obj({ after: num(0, 10), linear: num(0, 100), angular: num(0, 100), ramp: num(0, 10) }, ["after", "linear", "angular"], "Damp every part harder from `after` s on, rising over `ramp` s"),
          sleep: obj({ after: num(0, 10), speed: num(0.001, 3, "m/s"), spin: num(0.001, 20, "rad/s") }, ["after", "speed", "spin"], "From `after` s on, put a slowly moving body to rest"),
        },
        ["speed", "spin", "hold", "rest", "maxSeconds"],
      ),
      dissolve: obj({ seconds: num(0.05, 5) }, ["seconds"]),
    },
  };
}
